import { PieceColor, PieceType, Square, MoveDetail, OnlineRoomInfo } from '../core/types.ts';
import { NetworkMessage, CreateRoomMsg, JoinRoomMsg, MoveMsg, ResignMsg, DrawOfferMsg, DrawResponseMsg, RematchRequestMsg } from './protocol.ts';
import { ChessGame } from '../core/chessGame.ts';

export interface MultiplayerCallbacks {
  onRoomCreated?: (roomInfo: OnlineRoomInfo) => void;
  onRoomJoined?: (roomInfo: OnlineRoomInfo, fen: string) => void;
  onGameStart?: (whitePlayer: string, blackPlayer: string) => void;
  onMoveReceived?: (move: MoveDetail, fen: string) => void;
  onOpponentStatus?: (status: 'online' | 'disconnected' | 'reconnecting') => void;
  onDrawOffered?: () => void;
  onDrawResponse?: (accepted: boolean) => void;
  onRematchOffered?: () => void;
  onRematchResponse?: (accepted: boolean) => void;
  onResign?: (byColor: PieceColor) => void;
  onError?: (msg: string) => void;
}

export class MultiplayerClient {
  private ws: WebSocket | null = null;
  private channel: BroadcastChannel | null = null;
  private callbacks: MultiplayerCallbacks = {};
  private roomInfo: OnlineRoomInfo | null = null;
  private peerId: string;
  private localAuthorityGame: ChessGame | null = null;
  private isUsingLocalRelay = false;

  constructor(callbacks: MultiplayerCallbacks = {}) {
    this.callbacks = callbacks;
    this.peerId = `peer_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    this.initLocalRelay();
  }

  public setCallbacks(callbacks: MultiplayerCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  public get currentRoom(): OnlineRoomInfo | null {
    return this.roomInfo;
  }

  private initLocalRelay(): void {
    try {
      this.channel = new BroadcastChannel('wizard_chess_local_relay');
      this.channel.onmessage = (event) => {
        this.handleIncomingRaw(event.data);
      };
    } catch {
      console.warn('BroadcastChannel not supported in this environment');
    }
  }

  public connectWebSocket(serverUrl?: string): void {
    if (!serverUrl) return;

    try {
      this.ws = new WebSocket(serverUrl);
      this.ws.onopen = () => {
        this.isUsingLocalRelay = false;
        console.log('Connected to Multiplayer WebSocket Server');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      this.ws.onclose = () => {
        this.callbacks.onOpponentStatus?.('disconnected');
      };

      this.ws.onerror = (err) => {
        console.warn('WebSocket connection error, fallback to local relay:', err);
        this.isUsingLocalRelay = true;
      };
    } catch (e) {
      this.isUsingLocalRelay = true;
    }
  }

  private send(msg: NetworkMessage & { senderId?: string }): void {
    msg.senderId = this.peerId;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    } else if (this.channel) {
      this.isUsingLocalRelay = true;
      this.channel.postMessage(msg);
    }
  }

  private handleIncomingRaw(data: unknown): void {
    if (!data || typeof data !== 'object') return;
    const msg = data as NetworkMessage & { senderId?: string };
    if (msg.senderId === this.peerId) return; // ignore own broadcast
    this.handleMessage(msg);
  }

  private handleMessage(msg: NetworkMessage): void {
    switch (msg.type) {
      case 'ROOM_CREATED': {
        this.roomInfo = {
          roomId: msg.roomId,
          hostColor: msg.hostColor,
          myColor: msg.hostColor,
          isHost: true,
          status: 'waiting',
          opponentConnected: false,
          shareUrl: `${window.location.origin}${window.location.pathname}?room=${msg.roomId}`
        };
        this.callbacks.onRoomCreated?.(this.roomInfo);
        break;
      }

      case 'JOIN_ROOM': {
        // If we are the host running local relay
        if (this.roomInfo && this.roomInfo.isHost && this.roomInfo.roomId === msg.roomId) {
          const guestColor: PieceColor = this.roomInfo.hostColor === 'w' ? 'b' : 'w';
          this.roomInfo.opponentConnected = true;
          this.roomInfo.status = 'playing';

          // Reply with ROOM_JOINED
          this.send({
            type: 'ROOM_JOINED',
            roomId: msg.roomId,
            yourColor: guestColor,
            fen: this.localAuthorityGame ? this.localAuthorityGame.fen : 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
            history: []
          });

          // Send GAME_START
          this.send({
            type: 'GAME_START',
            roomId: msg.roomId,
            whitePlayer: this.roomInfo.hostColor === 'w' ? 'Host' : 'Guest',
            blackPlayer: this.roomInfo.hostColor === 'b' ? 'Host' : 'Guest',
            fen: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1'
          });

          this.callbacks.onOpponentStatus?.('online');
          this.callbacks.onGameStart?.('Host', 'Guest');
        }
        break;
      }

      case 'ROOM_JOINED': {
        this.roomInfo = {
          roomId: msg.roomId,
          hostColor: msg.yourColor === 'w' ? 'b' : 'w',
          myColor: msg.yourColor,
          isHost: false,
          status: 'playing',
          opponentConnected: true,
          shareUrl: `${window.location.origin}${window.location.pathname}?room=${msg.roomId}`
        };
        this.callbacks.onRoomJoined?.(this.roomInfo, msg.fen);
        break;
      }

      case 'GAME_START': {
        if (this.roomInfo) {
          this.roomInfo.status = 'playing';
          this.roomInfo.opponentConnected = true;
        }
        this.callbacks.onGameStart?.(msg.whitePlayer, msg.blackPlayer);
        break;
      }

      case 'MOVE': {
        // If we are host in local relay mode, authoritative validate
        if (this.roomInfo?.isHost && this.localAuthorityGame) {
          const detail = this.localAuthorityGame.makeMove(msg.from, msg.to, msg.promotion || 'q');
          if (detail) {
            this.send({
              type: 'MOVE_MADE',
              move: detail,
              fen: this.localAuthorityGame.fen,
              turn: this.localAuthorityGame.turn
            });
            this.callbacks.onMoveReceived?.(detail, this.localAuthorityGame.fen);
          } else {
            this.send({
              type: 'ERROR',
              message: 'Illegal move submitted'
            });
          }
        }
        break;
      }

      case 'MOVE_MADE': {
        this.callbacks.onMoveReceived?.(msg.move, msg.fen);
        break;
      }

      case 'RESIGN': {
        const resignedColor: PieceColor = this.roomInfo?.myColor === 'w' ? 'b' : 'w';
        this.callbacks.onResign?.(resignedColor);
        break;
      }

      case 'DRAW_OFFER': {
        this.callbacks.onDrawOffered?.();
        break;
      }

      case 'DRAW_RESPONSE': {
        this.callbacks.onDrawResponse?.(msg.accepted);
        break;
      }

      case 'REMATCH_REQUEST': {
        this.callbacks.onRematchOffered?.();
        break;
      }

      case 'REMATCH_RESPONSE': {
        this.callbacks.onRematchResponse?.(msg.accepted);
        break;
      }

      case 'PEER_STATUS': {
        if (this.roomInfo) {
          this.roomInfo.opponentConnected = msg.status === 'online';
        }
        this.callbacks.onOpponentStatus?.(msg.status);
        break;
      }

      case 'ERROR': {
        this.callbacks.onError?.(msg.message);
        break;
      }
    }
  }

  // --- Actions ---

  public createRoom(colorPreference: PieceColor | 'random' = 'random'): string {
    // Generate random 6-digit PIN (e.g., 472918)
    const roomId = Math.floor(100000 + Math.random() * 900000).toString();
    const assignedColor: PieceColor =
      colorPreference === 'random' ? (Math.random() > 0.5 ? 'w' : 'b') : colorPreference;

    this.localAuthorityGame = new ChessGame();

    const msg: CreateRoomMsg = {
      type: 'CREATE_ROOM',
      hostColorChoice: colorPreference
    };
    this.send(msg);

    // In local relay, trigger ROOM_CREATED immediately
    this.handleMessage({
      type: 'ROOM_CREATED',
      roomId,
      hostColor: assignedColor
    });

    return roomId;
  }

  public joinRoom(roomId: string): void {
    const cleanId = roomId.trim();
    if (cleanId.length !== 6) {
      this.callbacks.onError?.('Room PIN must be a 6-digit number');
      return;
    }

    const msg: JoinRoomMsg = {
      type: 'JOIN_ROOM',
      roomId: cleanId
    };
    this.send(msg);
  }

  public sendMove(from: Square, to: Square, promotion: PieceType = 'q'): void {
    if (!this.roomInfo) return;

    if (this.roomInfo.isHost && this.localAuthorityGame) {
      // Host validates locally
      const detail = this.localAuthorityGame.makeMove(from, to, promotion);
      if (detail) {
        this.send({
          type: 'MOVE_MADE',
          move: detail,
          fen: this.localAuthorityGame.fen,
          turn: this.localAuthorityGame.turn
        });
        this.callbacks.onMoveReceived?.(detail, this.localAuthorityGame.fen);
      } else {
        this.callbacks.onError?.('Illegal move');
      }
    } else {
      // Guest sends MOVE to host/server
      const msg: MoveMsg = {
        type: 'MOVE',
        roomId: this.roomInfo.roomId,
        from,
        to,
        promotion
      };
      this.send(msg);
    }
  }

  public resign(): void {
    if (!this.roomInfo) return;
    this.send({
      type: 'RESIGN',
      roomId: this.roomInfo.roomId
    });
    this.callbacks.onResign?.(this.roomInfo.myColor);
  }

  public offerDraw(): void {
    if (!this.roomInfo) return;
    this.send({
      type: 'DRAW_OFFER',
      roomId: this.roomInfo.roomId
    });
  }

  public respondDraw(accepted: boolean): void {
    if (!this.roomInfo) return;
    this.send({
      type: 'DRAW_RESPONSE',
      roomId: this.roomInfo.roomId,
      accepted
    });
  }

  public requestRematch(): void {
    if (!this.roomInfo) return;
    this.send({
      type: 'REMATCH_REQUEST',
      roomId: this.roomInfo.roomId
    });
  }

  public respondRematch(accepted: boolean): void {
    if (!this.roomInfo) return;
    if (accepted && this.localAuthorityGame) {
      this.localAuthorityGame.reset();
    }
    this.send({
      type: 'REMATCH_RESPONSE',
      roomId: this.roomInfo.roomId,
      accepted
    });
  }

  public leaveRoom(): void {
    if (this.roomInfo) {
      this.send({
        type: 'PEER_STATUS',
        status: 'disconnected'
      });
      this.roomInfo = null;
      this.localAuthorityGame = null;
    }
  }
}
